// src/components/Sidebar.js
import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../Context/AuthContext';
import { useAuthGate } from '../Context/AuthGateContext';
import { apiFetch } from '../services/apiClient';
import { FiLogOut, FiHelpCircle, FiVideo, FiX, FiMenu, FiPieChart, FiMessageCircle, FiRadio, FiShield } from 'react-icons/fi';
import { FaReddit, FaDiscord, FaInstagram, FaFacebook } from 'react-icons/fa';
import { TbFileBitcoin, TbHomeBitcoin, TbTransactionBitcoin, TbUserBitcoin, TbMoodBitcoin } from 'react-icons/tb';
import { MdOutlineVideogameAsset } from 'react-icons/md';
import './Sidebar.css';

function FaXTwitter(props) {
  return <svg viewBox="0 0 24 24" fill="currentColor" width="1em" height="1em" {...props}><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>;
}

const AVATAR_COLORS = ['#3b82f6','#16a34a','#ef4444','#8b5cf6','#f59e0b','#0ea5e9','#22c55e','#ec4899'];
function getAvatarColor(name) {
  return AVATAR_COLORS[(name?.charCodeAt(0) ?? 0) % AVATAR_COLORS.length];
}

// Rutas que un invitado puede ver sin sesión (app "abierta" + CTAs dentro)
const GUEST_ROUTES = ['/', '/portfolio', '/precios', '/muro'];

const NAV_GROUPS = [
  { label: 'Operar', items: [
    { to: '/',            icon: TbHomeBitcoin,        label: 'Marketplace', end: true },
    { to: '/precios',     icon: FiRadio,              label: 'Precios en vivo'        },
    { to: '/muro',        icon: FiMessageCircle,      label: 'Muro'                   },
    { to: '/create',      icon: TbFileBitcoin,        label: 'Crear oferta'           },
    { to: '/my-listings', icon: TbMoodBitcoin,        label: 'Mis ofertas'            },
  ]},
  { label: 'Mi cuenta', items: [
    { to: '/trades',      icon: TbTransactionBitcoin, label: 'Mis trades'             },
    { to: '/portfolio',   icon: FiPieChart,           label: 'Portafolio'             },
    { to: '/profile',     icon: TbUserBitcoin,        label: 'Mi perfil'              },
    { to: '/rewards',     icon: MdOutlineVideogameAsset, label: 'Recompensas'         },
  ]},
];

const HELP_ITEMS = [
  { to: 'https://www.reddit.com/r/Bitplify', icon: FaReddit,      label: 'Ir a Reddit',          ext: true },
  { to: '/faq',                               icon: FiHelpCircle,  label: 'Preguntas frecuentes', ext: false },
  { to: '/videos',                             icon: FiVideo,       label: 'Videos tutoriales',    ext: false },
  { to: 'https://discord.gg/Bitplify',        icon: FaDiscord,     label: 'Soporte en Discord',   ext: true },
];

// Un trade "requiere mi acción" si yo soy quien debe mover ahora
const needsMyAction = (t, uid) =>
  (t.status === 'WAITING_PAYMENT' && t.buyerId === uid) ||   // tengo que pagar
  (t.status === 'PAID'            && t.sellerId === uid);    // tengo que liberar

export default function Sidebar({ collapsed, toggleMenu }) {
  const { user, logout } = useAuth();
  const { openAuth }     = useAuthGate();
  const navigate         = useNavigate();

  const displayName = user?.name ?? user?.username ?? 'Usuario';
  const displayRole = user?.verified ? 'Vendedor verificado' : user?.rank ?? 'Trader';
  const initials    = displayName.slice(0, 2).toUpperCase();
  const avatarColor = getAvatarColor(displayName);
  // "Mi perfil" → perfil público por USERNAME (robusto; el id de la sesión puede estar desactualizado)
  const myProfilePath = user?.username ? `/profile/${user.username}` : '/profile';

  // Badge de "acción requerida" en Mis trades
  const [actionCount, setActionCount] = useState(0);
  useEffect(() => {
    if (!user) { setActionCount(0); return; }
    let cancel = false;
    (async () => {
      try {
        const res = await apiFetch('/api/trades');
        if (!res.ok) return;
        const trades = await res.json();
        if (!cancel) setActionCount((Array.isArray(trades) ? trades : []).filter(t => needsMyAction(t, user.id)).length);
      } catch {}
    })();
    return () => { cancel = true; };
  }, [user]);

  // Precio BTC en vivo para el "pulso" del mercado
  const [btc, setBtc] = useState(null);
  const [btcDir, setBtcDir] = useState(0);
  useEffect(() => {
    let prev = null, cancel = false;
    const fetchBtc = async () => {
      try {
        const r = await fetch('https://data-api.binance.vision/api/v3/ticker/price?symbol=BTCUSDT');
        const d = await r.json();
        const raw = parseFloat(d.price);
        if (cancel || !raw) return;
        if (prev != null && raw !== prev) setBtcDir(raw > prev ? 1 : -1);
        prev = raw;
        setBtc(raw.toLocaleString('en-US', { maximumFractionDigits: 0 }));
      } catch {}
    };
    fetchBtc();
    const t = setInterval(fetchBtc, 20000);
    return () => { cancel = true; clearInterval(t); };
  }, []);

  const handleLogout = (e) => {
    e.preventDefault();
    e.stopPropagation();
    logout();
    navigate('/login');
  };

  const navClass = ({ isActive }) => `sb-navlink${isActive ? ' active' : ''}`;

  return (
    <>
      {/* Toggle button */}
      <button className="sb-toggle" onClick={toggleMenu} aria-label={collapsed ? 'Abrir menú' : 'Cerrar menú'}>
        <FiMenu />
      </button>

      <aside className={`sb${collapsed ? ' sb-collapsed' : ''}`}>
        {/* Close button — CSS handles mobile visibility */}
        <button className="sb-close" onClick={toggleMenu} aria-label="Cerrar menú">
          <FiX />
        </button>

        {/* Profile card (logueado) o CTA de invitado */}
        {user ? (
          <div className="sb-profile-wrap">
            <NavLink to={myProfilePath} className="sb-profile" onClick={toggleMenu}>
              <div className="sb-avatar" style={{ background: user?.avatar ? 'transparent' : avatarColor }}>
                {user?.avatar
                  ? <img src={user.avatar} alt="avatar" className="sb-avatar-img" />
                  : initials
                }
              </div>
              <div className="sb-profile-info">
                <strong>{displayName}</strong>
                <span>{user?.verified && <span className="sb-verified-dot" />}{displayRole}</span>
              </div>
            </NavLink>
            <button className="sb-logout" onClick={handleLogout} title="Cerrar sesión">
              <FiLogOut />
            </button>
          </div>
        ) : (
          <div className="sb-guest">
            <div className="sb-guest-info">
              <strong>Invitado</strong>
              <span>Entra para operar</span>
            </div>
            <div className="sb-guest-btns">
              <button className="sb-guest-btn solid" onClick={() => { openAuth('register'); toggleMenu(); }}>Registrarse</button>
              <button className="sb-guest-btn ghost" onClick={() => { openAuth('login'); toggleMenu(); }}>Entrar</button>
            </div>
          </div>
        )}

        {/* CTA de verificación — solo logueado y NO verificado (empuja el embudo de venta) */}
        {user && !user.verified && (
          <a href="https://discord.gg/Bitplify" target="_blank" rel="noopener noreferrer" className="sb-verify-cta" onClick={toggleMenu}>
            <FiShield className="sb-verify-ic" />
            <span className="sb-verify-txt"><strong>Verificá tu cuenta</strong><span>Pedí acceso para vender →</span></span>
          </a>
        )}

        {/* Pulso de mercado en vivo — ahora con precio BTC real */}
        <div className="sb-pulse">
          <span className="sb-pulse-dot" />
          {btc
            ? <span className="sb-pulse-live">BTC <span className={`sb-pulse-price ${btcDir > 0 ? 'up' : btcDir < 0 ? 'down' : ''}`}>${btc}</span></span>
            : <span className="sb-pulse-label">Mercado P2P en vivo</span>}
        </div>

        {/* Main nav — agrupado */}
        {NAV_GROUPS.map(group => (
          <div key={group.label} className="sb-navgroup">
            <div className="sb-section-title">{group.label}</div>
            <ul className="sb-nav">
              {group.items.map(({ to, icon: Icon, label, end }, i) => (
                <li key={to} style={{ animationDelay: `${i * 40}ms` }}>
                  <NavLink to={to === '/profile' ? myProfilePath : to} end={end} className={navClass} onClick={(e) => {
                    if (!user && !GUEST_ROUTES.includes(to)) { e.preventDefault(); openAuth('register'); }
                    toggleMenu();
                  }}>
                    <span className="sb-icon-box"><Icon className="sb-icon" /></span>
                    <span className="sb-label">{label}</span>
                    {to === '/trades' && actionCount > 0 && <span className="sb-badge" title={`${actionCount} trade(s) requieren tu acción`}>{actionCount}</span>}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}

        {/* Help section */}
        <div className="sb-help">
          <div className="sb-help-title">Centro de ayuda</div>
          <ul className="sb-help-menu">
            {HELP_ITEMS.map(({ to, icon: Icon, label, ext }) => (
              <li key={to}>
                {ext ? (
                  <a href={to} target="_blank" rel="noopener noreferrer" onClick={toggleMenu}>
                    <Icon className="sb-help-icon" /><span>{label}</span>
                  </a>
                ) : (
                  <NavLink to={to} className={navClass} onClick={toggleMenu}>
                    <Icon className="sb-help-icon" /><span>{label}</span>
                  </NavLink>
                )}
              </li>
            ))}
          </ul>

          <div className="sb-social">
            <a href="https://x.com/Bitplify" target="_blank" rel="noopener noreferrer" className="sb-social-btn" title="X"><FaXTwitter /></a>
            <a href="https://instagram.com/Bitplify" target="_blank" rel="noopener noreferrer" className="sb-social-btn" title="Instagram"><FaInstagram /></a>
            <a href="https://facebook.com/Bitplify" target="_blank" rel="noopener noreferrer" className="sb-social-btn" title="Facebook"><FaFacebook /></a>
            <a href="https://discord.gg/Bitplify" target="_blank" rel="noopener noreferrer" className="sb-social-btn" title="Discord"><FaDiscord /></a>
          </div>
        </div>
      </aside>

      {!collapsed && <div className="sb-backdrop" onClick={toggleMenu} />}
    </>
  );
}
